<template>
  <section class="h-full mx-4" id="documentTypesManagement">
    <BaseTable
      v-if="documentTypes"
      :headers="documentTypesHeaders"
      :list="documentTypes"
      clickable
      @deleteMultiselect="onDeleteMultiselect"
      @add-new="onAddNew"
      @refresh-data="refreshData"
      @table-item-clicked="onItemClick"
      table-id="document-types"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters">
      <template #actions="{ item }">
        <div class="flex items-center justify-center gap-1">
          <ActionButton
            :isEnabled="item.isFromManager"
            color="red"
            :icon="IconTrashOutline"
            @action="onDeleteRow(item as DocumentType)" />
        </div>
      </template>

      <template #filters>
        <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
          <div>
            <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
              $t('baseObject.isActive')
            }}</label>
            <BaseTriStateToggle
              :label="$t('baseObject.isActive')"
              name="isActive"
              v-model="filterIsActive" />
          </div>
        </section>
      </template>
    </BaseTable>

    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()">
      <template #title>{{ $t('common.actions.delete') }}</template>
      <template #text> {{ $t('messages.info.deleteCascade') }}</template>
      <template #buttonText>{{ $t('common.actions.delete') }}</template>
    </BaseModalConfirm>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { useRouter } from 'vue-router';
import { useToastStore } from '@/stores/toast';
import { getDocumentTypes, removeDocumentTypesByIds } from '@/services/api/documents';
import { filterActive, filterData, sortData } from '@/utils/table';

import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import ActionButton from '@/components/common/ActionButton.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';

import { type DocumentType } from '@/types/DocumentTypes';
import type { TableFilterData } from '@/types/Base';
import { useLookupsStore } from '@/stores/lookups';
import { useI18n } from 'vue-i18n';

const { t: $t } = useI18n({ useScope: 'global' });

const { reloadLookups } = useLookupsStore();

const toastStore = useToastStore();
const router = useRouter();
const documentTypes = ref<DocumentType[]>();
const filterIsActive = ref<boolean | undefined>(undefined);

const documentTypesHeaders = [
  { value: 'code', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'division.name', text: $t('document-type.division'), type: 'text', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'status', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

onMounted(async () => {
  await loadData();
});

async function loadData() {
  const res = await getDocumentTypes();
  if (res) {
    documentTypes.value = res;
  }
}

const modalDeleteRow = ref(false);
const deleteRowItem = ref<DocumentType>();
function onDeleteRow(item: DocumentType) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

async function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!deleteRowItem.value) return;
  const idToDelete = deleteRowItem.value.id;
  const res = await removeDocumentTypesByIds([idToDelete]);
  if (!res && documentTypes.value)
    documentTypes.value = documentTypes.value.filter((type) => type.id !== idToDelete);
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });

  await reloadLookups(['documentTypes']);
}

async function onDeleteMultiselect(items: DocumentType[]) {
  const idsToDelete = items.filter((item) => item.isFromManager).map((item) => item.id);
  const res = await removeDocumentTypesByIds(idsToDelete);
  if (!res && documentTypes.value)
    documentTypes.value = documentTypes.value.filter((type) => !idsToDelete.includes(type.id));
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });

  await reloadLookups(['documentTypes']);
}

async function refreshData(data: TableFilterData) {
  await loadData();
  if (data.query && documentTypes.value) {
    documentTypes.value = filterData(documentTypes.value, data.query, [
      'name',
      'code',
      'division.name',
    ]);
  }

  if (data.column !== undefined && data.order && documentTypes.value) {
    documentTypes.value = sortData(
      documentTypes.value,
      data.column,
      data.order,
      documentTypesHeaders
    );
  }

  if (data.isActive !== undefined && documentTypes.value)
    documentTypes.value = filterActive(documentTypes.value, data.isActive);
}

function onAddNew() {
  router.push({ name: 'document-types-details', params: { id: uuidv4() } });
}

function onItemClick(item: DocumentType) {
  if (!item.deleted)
    router.push({
      name: 'document-types-details',
      params: { id: item.id },
    });
}

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value ?? undefined;
  refreshData(data);
}

function onResetFilters() {
  filterIsActive.value = undefined;
  refreshData({});
}
</script>
